import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";

import { getWhatnotUser } from "src/utils/extension/whatnot";
import type { Cookie } from "src/utils/extension";
import {
  getWhatnotIntegration,
  whatnotIntegrationCreate,
} from "src/apiService/modules/whatnot";
import { AddNewSalePlatformIntegrationButton } from "src/components/AddNewSalePlatformIntegrationDialog";

import DialogTitle from "../DialogTitle";
import type Step from "../Step";
import { useRecordEvent } from "src/hooks/useRecordEvent";

interface WhatnotConfirmProps {
  onClose: () => void;
  onStepChange: (step: Step) => void;
}

function WhatnotConfirm({ onClose, onStepChange }: WhatnotConfirmProps) {
  const recordEvent = useRecordEvent();
  const [whatnotUserData, setWhatnotUserData] = useState<
    | {
        username: string;
        userId: string;
        cookies: Cookie[];
      }
    | { error: string }
    | { loading: true }
  >({ loading: true });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await getWhatnotUser();
        if (cancel) return;
        setWhatnotUserData(data);
      } catch (e) {
        if (cancel) return;
        setWhatnotUserData({ error: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <>
      <DialogTitle onClose={onClose}>Add Whatnot integration</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          {"username" in whatnotUserData
            ? `The Whatnot account "${whatnotUserData.username}" will be added.`
            : "error" in whatnotUserData
            ? `ERROR: ${whatnotUserData.error}`
            : "Loading..."}
        </DialogContentText>
      </MuiDialogContent>
      <DialogActions>
        <Button onClick={() => onStepChange("whatnot:do-you-sell-on-whatnot")}>
          Cancel
        </Button>
        <AddNewSalePlatformIntegrationButton
          variant="contained"
          disabled={
            !!(whatnotUserData as any).error || (whatnotUserData as any).loading
          }
          onConnect={async (bankAccountId) => {
            if ("cookies" in whatnotUserData && whatnotUserData.cookies) {
              const existingWhatnotIntegration = await getWhatnotIntegration();
              if (existingWhatnotIntegration?.id) {
                toast.info("That Whatnot account is already connected.");
                return;
              }
              await whatnotIntegrationCreate({
                cookies: whatnotUserData.cookies,
                username: whatnotUserData?.username,
                lastSync: new Date().toISOString(),
                bankAccountId,
                whatnotUserId: whatnotUserData.userId,
              });
              recordEvent({
                type: "feature-adoption",
                feature: "integration-added",
                element: "sale-platform-whatnot",
              });
              onStepChange("whatnot:import");
            }
          }}
          color="primary"
          autoFocus
        >
          Add
        </AddNewSalePlatformIntegrationButton>
      </DialogActions>
    </>
  );
}

export default WhatnotConfirm;
